/**
 * 1.69 (198) — a guest's requests as the staff see them: a call, an order from the menu, a bill request. The server
 * decides who may see and act (`table_requests_open`, `audience` read from the party's visit at read time); these rules
 * only shape the list and pick the card's one button.
 */
export type RequestKind = 'call' | 'order' | 'check'
export type RequestStatus = 'waiting' | 'taken' | 'done' | 'dismissed'
/** mine = the reader is the party's waiter · other = another available waiter has the party · all = nobody has it */
export type RequestAudience = 'mine' | 'other' | 'all'

export type RequestLine = {
  id: string
  /** null when the product was deleted since the guest sent it (the confirm shows it at 0, not adjustable) */
  productId: string | null
  name: string | null
  nameEn: string | null
  qty: number
  note: string | null
  unit: string | null
  deviceId: string | null
}

export type GuestRequest = {
  id: string
  branchId: string
  tableId: string
  visitId: string
  kind: RequestKind
  status: RequestStatus
  /** rises on every change of the order's lines — the confirm sends back the one it showed */
  version: number
  createdAt: string
  /** the newest ring (a repeat after 60 s rings again) */
  rangAt: string | null
  takenBy: string | null
  takenByName: string | null
  takenAt: string | null
  closeReason: string | null
  audience: RequestAudience
  waiterId: string | null
  waiterName: string | null
  lines: RequestLine[]
}

const KINDS = new Set<RequestKind>(['call', 'order', 'check'])
const STATUSES = new Set<RequestStatus>(['waiting', 'taken', 'done', 'dismissed'])
const str = (v: unknown) => (v == null ? null : String(v))

function mapLine(l: any): RequestLine {
  return {
    id: String(l?.id ?? ''), productId: l?.product_id ?? null, name: str(l?.name), nameEn: str(l?.name_en),
    qty: Number(l?.qty) || 0, note: str(l?.note), unit: str(l?.unit), deviceId: l?.device_id ?? null,
  }
}

export function mapRequests(raw: unknown): GuestRequest[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((r) => r && r.id && r.table_id && r.visit_id && KINDS.has(r.kind) && STATUSES.has(r.status))
    .map((r: any) => ({
      id: String(r.id), branchId: String(r.branch_id ?? ''), tableId: String(r.table_id), visitId: String(r.visit_id),
      kind: r.kind, status: r.status, version: Number(r.version) || 0, createdAt: String(r.created_at ?? ''),
      rangAt: r.rang_at ?? null, takenBy: r.taken_by ?? null, takenByName: str(r.taken_by_name), takenAt: r.taken_at ?? null,
      closeReason: str(r.close_reason),
      // an audience this client does not know is never shown to a waiter as his
      audience: r.audience === 'mine' || r.audience === 'all' ? r.audience : 'other',
      waiterId: r.waiter_id ?? null, waiterName: str(r.waiter_name),
      lines: Array.isArray(r.lines) ? r.lines.map(mapLine) : [],
    }))
}

const open = (r: GuestRequest) => r.status === 'waiting' || r.status === 'taken'
const oldestFirst = (a: GuestRequest, b: GuestRequest) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)

/** A waiter sees his own party's requests and the unclaimed ones, never another waiter's; the till sees every one.
 *  `audience` is the server's answer for the user who read the rows, so the reader needs no id here. */
export function requestsFor(reqs: readonly GuestRequest[], isTill: boolean): GuestRequest[] {
  return reqs.filter((r) => open(r) && (isTill || r.audience !== 'other')).sort(oldestFirst)
}

/** The card's one button: claim („შეკვეთის აღება") a party nobody has; on his own party a call → „მივდივარ", an order
 *  → „ნახვა", a bill request → „ანგარიშის ბეჭდვა". The till never claims — a claim from a till makes nobody the waiter
 *  (198), so the till acts at once. */
export function requestAction(r: GuestRequest, isTill = false): 'claim' | 'coming' | 'open' | 'print' {
  if (!isTill && r.audience === 'all') return 'claim'
  return r.kind === 'call' ? 'coming' : r.kind === 'order' ? 'open' : 'print'
}

/** One ring = one key: a device chimes once per key (a repeat ring after 60 s is a new key). */
export const ringKey = (r: GuestRequest): string => `${r.id}:${r.rangAt ?? r.createdAt}`

/** From this many minutes after a request came it is shown amber (waiting for us — never red). */
export const REQUEST_LATE_MIN = 5

/** Counted from when it came, so a repeat ring does not make an old request look new. */
export const isLateRequest = (r: GuestRequest, now: Date): boolean =>
  now.getTime() - Date.parse(r.createdAt) >= REQUEST_LATE_MIN * 60_000

/** 1.70 (the 1.69 review's A11) — a confirm's automatic add-ons are computed on the confirming device from its copy of
 *  the add-on rules (plan R1); the server refuses a set it does not expect (`REQUEST_ADDON_INVALID`). A device whose
 *  copy is older than the owner's last change stayed refused until a reload. Now: re-read the rules once and send
 *  again (`send(1)` computes the add-ons afresh); any other answer, a failed re-read or a second refusal is the
 *  answer — never a loop. `alive` = the session that asked is still the one signed in (the 1.70 review's M3): a
 *  sign-out or another shop between the sends ends it with the first answer. */
export async function confirmWithFreshAddons<R extends { ok: boolean; code?: string | null }>(
  send: (attempt: 0 | 1) => Promise<R>, refresh: () => Promise<boolean>, alive: () => boolean = () => true,
): Promise<R> {
  const first = await send(0)
  if (first.ok || first.code !== 'REQUEST_ADDON_INVALID' || !alive()) return first
  if (!(await refresh()) || !alive()) return first
  return send(1)
}

/** 1.71 (the 1.70 review's M2) — what the confirm sheet does after the server refused. It used to read the order again
 *  and say „შეკვეთა შეიცვალა" after ANY refusal, so a connection lost for a second wiped the quantities the waiter had
 *  just agreed with the guest. Now: the guest changed the order → read it again; the order is no longer this waiter's
 *  to confirm (closed, another waiter's table, another branch) → close the sheet (the store re-reads the list);
 *  anything else — a lost connection (pressing again sends the same op), everything at zero, add-ons — keeps the sheet
 *  as the waiter left it. The refusal's own toast has already said why. */
export type ConfirmRefusal = 'reread' | 'close' | 'stay'
// the confirm's own refusals (198 `table_request_confirm`); `REQUEST_TAKEN` is a claim's answer, never a confirm's
const CONFIRM_CLOSES = new Set(['REQUEST_CLOSED', 'REQUEST_NOT_YOURS', 'TABLE_BRANCH_FORBIDDEN'])
export function afterConfirmRefusal(code: string | null): ConfirmRefusal {
  if (code === 'REQUEST_STALE') return 'reread'
  return code !== null && CONFIRM_CLOSES.has(code) ? 'close' : 'stay'
}

/** 1.71 (review m2) — the op of a confirm is the request AND the quantities sent. A lost answer keeps its op for the
 *  next press (`pendingOp`); with the sheet now staying open the waiter may change a quantity before pressing again,
 *  and the same op would get the FIRST send's stored result back — „გაიგზავნა" over the old quantities. A changed set
 *  is a new op: the server either takes it (the first never arrived) or answers `REQUEST_CLOSED` (it did) — the truth. */
export function confirmOpKey(requestId: string, lines: { line_id: string; qty: number }[]): string {
  return `request:${requestId}:${lines.map((l) => `${l.line_id}=${l.qty}`).join(',')}`
}
